import type { InternalRequest } from '../client.js';

type Params = Record<string, string | number | boolean | undefined | null>;

function qsFrom(params?: Params): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of entries) usp.set(k, String(v));
  return `?${usp}`;
}

/**
 * /api/form-submissions — unified surface, replaces /api/inspections/submissions.
 * A submission attaches to a Work (parentWorkId), a Work Stop (parentStopId),
 * or neither (free-floating). Server validates org-scoped FKs on create.
 */
export function formSubmissionsEndpoints(req: InternalRequest) {
  return {
    list: (params?: Params) =>
      req.request<unknown[]>(`/form-submissions${qsFrom(params)}`),
    get: (id: string) => req.request<unknown>(`/form-submissions/${id}`),
    create: (data: {
      formId: string;
      parentWorkId?: string;
      parentStopId?: string;
      answers: Record<string, unknown>;
      /**
       * The instant + fix captured on-device at fill time (mobile's
       * formCapture.ts), not when the request finally reaches the server —
       * this is what survives the offline queue. Server: `occurredAt` a full
       * ISO instant (malformed/too-skewed falls back to server clock, never
       * rejected); `location` is `{lat, lng, accuracy?}` — NOT
       * `{latitude, longitude}` — anything else stored as null.
       */
      occurredAt?: string;
      location?: { lat: number; lng: number; accuracy?: number } | null;
      /**
       * When and where the form was OPENED (mobile's captureFormStart), so
       * the server can derive how long it was open and whether the worker
       * moved. Same lenience as occurredAt: malformed, after occurredAt, or
       * >24 h before it is stored null, never rejected. Ignored unless
       * occurredAt is also supplied — the pair must share one clock.
       */
      startedAt?: string;
      startedLocation?: { lat: number; lng: number; accuracy?: number } | null;
      deviceId?: string;
      clientId?: string;
    }) =>
      req.request<unknown>('/form-submissions', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  };
}
